"use client"

import { createResourceRequest } from "@/components/resource-requests/create/actions"
import { ResourceRequestStepper } from "@/components/resource-requests/create/resource-request-stepper"
import { AdditionalInfoForm, AdditionalInfoFormData } from "@/components/resource-requests/create/steps/additional-info-form"
import { BasicInfoForm } from "@/components/resource-requests/create/steps/basic-info-form"
import { CompensationTimelineForm } from "@/components/resource-requests/create/steps/compensation-timeline-form"
import { RequirementsForm } from "@/components/resource-requests/create/steps/requirements-form"
import { ReviewSubmitForm } from "@/components/resource-requests/create/steps/review-submit-form"
import { WorkingConditionsForm } from "@/components/resource-requests/create/steps/working-conditions-form"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { useState } from "react"

// Using string literals to match the AdditionalInfoFormData enum values
type PriorityType = "low" | "medium" | "high" | "urgent"
type StatusType = "open" | "pending" | "closed"

const steps = [
  {
    title: "Basic Info",
    description: "General request information",
  },
  {
    title: "Requirements",
    description: "Position requirements",
  },
  {
    title: "Working Conditions",
    description: "Work location and mode",
  },
  {
    title: "Additional Info",
    description: "Project details and notes",
  },
  {
    title: "Compensation & Timeline",
    description: "Salary and deadlines",
  },
  {
    title: "Review & Submit",
    description: "Submit your request",
  },
]

// Create a type representing the entire form data
interface ResourceRequestFormData {
  // Basic info
  position: string;
  quantity: number;
  skills: string[];
  department: string;
  
  // Requirements
  educationLevel: string;
  experienceYears: number;
  certifications: string[];
  technicalSkills: string[];
  
  // Working conditions
  workMode: 'office' | 'remote' | 'hybrid';
  locationType: 'domestic' | 'international';
  travelRequirements: string;
  
  // Additional info
  projectCode: string;
  notes: string;
  requiresCodeLiveTest: boolean;
  priority: PriorityType;
  status: StatusType;
  
  // Compensation & timeline
  salary: {
    min: number;
    max: number;
    currency: string;
    range: 'below_market' | 'market' | 'above_market';
  };
  budget: number;
  targetStartDate: string;
  targetEndDate: string;
  requestDeadline: string;
}

export default function CreateResourceRequestPage() {
  const { toast } = useToast()
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<ResourceRequestFormData>({
    // Basic info defaults
    position: "",
    quantity: 1,
    skills: [],
    department: "",
    
    // Requirements defaults
    educationLevel: "",
    experienceYears: 0,
    certifications: [],
    technicalSkills: [],
    
    // Working conditions defaults
    workMode: "office",
    locationType: "domestic",
    travelRequirements: "",
    
    // Additional info defaults
    projectCode: "",
    notes: "",
    requiresCodeLiveTest: false,
    priority: "medium",
    status: "open",
    
    // Compensation & timeline defaults
    salary: {
      min: 0,
      max: 0,
      currency: "USD",
      range: "market"
    },
    budget: 0,
    targetStartDate: "",
    targetEndDate: "",
    requestDeadline: ""
  })

  const updateFormData = (data: Partial<ResourceRequestFormData>) => {
    setFormData(prev => ({ ...prev, ...data }))
  }

  // Typed update functions for specific form sections
  const updateAdditionalInfo = (data: AdditionalInfoFormData) => {
    updateFormData(data)
  }

  const goToStep = (step: number) => {
    setCurrentStep(step)
  }

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1))
  }

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = async () => {
    try {
      // Transform form data to match API expectations
      const apiPayload = {
        title: formData.position,
        description: formData.notes,
        urgency: formData.priority as "low" | "medium" | "high",
        requiredBy: formData.requestDeadline ? new Date(formData.requestDeadline) : undefined
      };
      
      await createResourceRequest(apiPayload);
      
      toast({
        title: "Resource request created",
        description: "Your resource request has been submitted successfully",
      })
      // Redirect to resource requests list or reset form
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create resource request",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Create Resource Request</h1>
        <p className="text-muted-foreground">
          Fill out the form to create a new resource request
        </p>
      </div>

      <ResourceRequestStepper 
        steps={steps} 
        currentStep={currentStep} 
        onStepClick={goToStep} 
      />
      
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>{steps[currentStep].title}</CardTitle>
          <CardDescription>{steps[currentStep].description}</CardDescription>
        </CardHeader>
        <CardContent>
          {currentStep === 0 && (
            <BasicInfoForm 
              formData={formData} 
              updateFormData={updateFormData} 
              onNext={handleNext}
              isFirstStep={true}
            />
          )}
          {currentStep === 1 && (
            <RequirementsForm 
              formData={formData} 
              updateFormData={updateFormData} 
              onNext={handleNext} 
              onPrevious={handlePrevious} 
            />
          )}
          {currentStep === 2 && (
            <WorkingConditionsForm 
              formData={formData} 
              updateFormData={updateFormData} 
              onNext={handleNext} 
              onPrevious={handlePrevious} 
            />
          )}
          {currentStep === 3 && (
            <AdditionalInfoForm 
              formData={{
                projectCode: formData.projectCode,
                notes: formData.notes,
                requiresCodeLiveTest: formData.requiresCodeLiveTest,
                priority: formData.priority,
                status: formData.status
              }} 
              updateFormData={updateAdditionalInfo} 
              onNext={handleNext} 
              onPrevious={handlePrevious} 
            />
          )}
          {currentStep === 4 && (
            <CompensationTimelineForm 
              formData={{
                salary: formData.salary,
                budget: formData.budget,
                targetStartDate: formData.targetStartDate,
                targetEndDate: formData.targetEndDate,
                requestDeadline: formData.requestDeadline
              }} 
              updateFormData={updateFormData} 
              onNext={handleNext} 
              onPrevious={handlePrevious} 
            />
          )}
          {currentStep === 5 && (
            <ReviewSubmitForm 
              formData={formData} 
              onSubmit={handleSubmit} 
              onPrevious={handlePrevious}
              isLastStep={true}
            />
          )}
        </CardContent>
      </Card>
    </div>
  )
} 